import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Share2, Trash2, Workflow, Plus } from 'lucide-react';
import { useDiagramList, useDeleteDiagram } from '../features/diagrams/hooks/useDiagram';
import { StaggerList, ListItem } from '../components/ui/Motion.js';

// Offscreen thumbnail generator (lazy so maxGraph only loads when needed).
const DiagramThumbnailGenerator = lazy(
  () => import('../features/diagrams/components/DiagramThumbnailGenerator'),
);

const TOP_COLORS = [
  'border-t-xd-info',
  'border-t-xd-action',
  'border-t-xd-action-end',
  'border-t-xd-badge',
];

const KIT_ROLES: Record<string, string> = {
  aws: 'AWS',
  uml: 'UML',
  bpmn: 'BPMN',
};

/**
 * Card thumbnail: the auto-generated PNG when available, otherwise the
 * gradient + icon fallback (also on img load error).
 */
function DiagramThumb({
  diagramId,
  src,
  pageCount,
}: {
  diagramId: string;
  src?: string;
  pageCount: number;
}) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <div className="aspect-video">
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  return (
    <>
      {!failed && (
        <Suspense fallback={null}>
          <DiagramThumbnailGenerator diagramId={diagramId} />
        </Suspense>
      )}
      <div className="aspect-video bg-gradient-to-br from-xd-badge/10 to-xd-info/10 flex flex-col items-center justify-center gap-2 group-hover:from-xd-badge/20 group-hover:to-xd-info/20 transition-colors">
        <Workflow className="h-8 w-8 text-xd-badge/50 group-hover:text-xd-badge transition-colors" />
        <span className="text-xs text-text-subtle group-hover:text-text-muted transition-colors">
          {pageCount} {pageCount === 1 ? 'página' : 'páginas'}
        </span>
      </div>
    </>
  );
}

interface DiagramListProps {
  /** When true, renders as a tab panel inside Visuales (no own header). */
  embedded?: boolean;
  onNew?: () => void;
}

export function DiagramList({ embedded = false, onNew }: DiagramListProps) {
  const navigate = useNavigate();
  const { data: diagrams, isLoading } = useDiagramList();
  const deleteDiagram = useDeleteDiagram();

  const handleNew = () => (onNew ? onNew() : navigate('/diagrams/new'));

  const handleDelete = (e: React.MouseEvent, diagramId: string) => {
    e.stopPropagation();
    deleteDiagram.mutate(diagramId);
  };

  return (
    <div className={embedded ? '' : 'page-enter'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-text">Diagramas</h2>
            <p className="text-sm text-text-muted mt-0.5">
              {diagrams ? `${diagrams.length} diagramas` : 'Diagramas de arquitectura y UML'}
            </p>
          </div>
          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 rounded-lg bg-xd-action px-4 py-2 text-sm font-semibold text-[var(--xds-fg)] hover:bg-xd-action-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuevo diagrama
          </button>
        </div>
      )}

      {/* Skeleton loading */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl overflow-hidden">
              <div className="aspect-video bg-surface-2 animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-2 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-surface-2 rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!diagrams || diagrams.length === 0) && (
        <div className="flex flex-col items-center py-24 border-2 border-dashed border-border rounded-2xl text-center">
          <Workflow className="h-10 w-10 text-text-subtle" />
          <p className="text-text-muted text-base mt-4">Aún no hay diagramas</p>
          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 rounded-lg bg-xd-action px-4 py-2 text-sm font-semibold text-[var(--xds-fg)] hover:bg-xd-action-hover transition-colors mt-5"
          >
            Crear diagrama
          </button>
        </div>
      )}

      {/* Grid */}
      {diagrams && diagrams.length > 0 && (
        <StaggerList className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {diagrams.map((diagram, index) => (
            <ListItem key={diagram.id}>
              <button
                onClick={() => navigate(`/diagrams/${diagram.id}`)}
                className={`card-hover group block w-full h-full text-left bg-surface border border-border rounded-xl overflow-hidden border-t-2 ${TOP_COLORS[index % TOP_COLORS.length]}`}
              >
                {/* Thumbnail */}
                <DiagramThumb
                  key={diagram.thumbnailUrl ?? 'fallback'}
                  diagramId={diagram.id}
                  src={diagram.thumbnailUrl}
                  pageCount={diagram.pageCount}
                />

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-text truncate">{diagram.title}</h3>
                    <button
                      onClick={(e) => handleDelete(e, diagram.id)}
                      className="inline-flex items-center gap-2 rounded-lg text-sm font-medium text-xd-error hover:bg-xd-error/10 px-1.5 py-1 transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Eliminar diagrama"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-text-subtle mt-1">
                    Actualizado{' '}
                    {formatDistanceToNow(new Date(diagram.updatedAt), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </p>

                  {/* Kits */}
                  {diagram.kits.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {diagram.kits.slice(0, 3).map((kit) => (
                        <span
                          key={kit}
                          className="px-2 py-0.5 bg-surface-2 text-text-muted text-xs rounded-full flex items-center gap-1"
                        >
                          <Share2 className="h-3 w-3" />
                          {KIT_ROLES[kit] ?? kit}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            </ListItem>
          ))}
        </StaggerList>
      )}
    </div>
  );
}
